import React, { useState } from "react";
import { Lock, ShieldCheck, Send } from "lucide-react";
import { client } from "@/lib/api";
import { PageHero } from "@/components/blocks/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const PrayerRequests = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", category: "general", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error("Please share your name and prayer request.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await client.post("/prayer-requests", payload);
      setDone(true);
      toast.success("Your prayer request has been received.");
      setForm({ name: "", email: "", phone: "", category: "general", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHero
        overline="Prayer Requests"
        title="We stand with you in prayer"
        subtitle="Submit a confidential prayer request. Our intercessory team will lift you up."
        image="https://images.unsplash.com/photo-1475527588268-e6a157656e35?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
      />
      <section className="bg-white py-24 md:py-28">
        <div className="mx-auto grid max-w-6xl gap-16 px-5 lg:grid-cols-5 lg:px-8">
          <div className="lg:col-span-2">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-sm bg-navy text-brand-orange">
              <Lock className="h-6 w-6" />
            </span>
            <h2 className="mt-6 font-display text-2xl font-bold text-navy">Confidential & Private</h2>
            <p className="mt-3 text-muted-foreground">
              Your request is kept private and is never displayed publicly. It is shared only with our trained prayer and counselling team.
            </p>
            <div className="mt-6 flex items-start gap-3 border border-border bg-secondary/40 p-5">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-orange" />
              <p className="text-sm text-muted-foreground">We count because every soul counts. Let’s no one get lost.</p>
            </div>
          </div>

          <div className="lg:col-span-3">
            {done ? (
              <div data-testid="prayer-success" className="border border-border bg-secondary/40 p-10 text-center">
                <ShieldCheck className="mx-auto h-12 w-12 text-brand-orange" />
                <h3 className="mt-5 font-display text-2xl font-bold text-navy">Received</h3>
                <p className="mt-2 text-muted-foreground">Thank you. Your prayer request has been submitted confidentially.</p>
                <Button onClick={() => setDone(false)} className="mt-6 rounded-sm bg-navy uppercase tracking-wide hover:bg-navy-surface">Submit another</Button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="prayer-form" className="space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input id="name" data-testid="prayer-name" value={form.name} onChange={update("name")} className="mt-2 rounded-sm" placeholder="Your name" />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone (optional)</Label>
                    <Input id="phone" data-testid="prayer-phone" value={form.phone} onChange={update("phone")} className="mt-2 rounded-sm" placeholder="Phone number" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email (optional)</Label>
                  <Input id="email" type="email" data-testid="prayer-email" value={form.email} onChange={update("email")} className="mt-2 rounded-sm" placeholder="you@email.com" />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="prayer-category" className="mt-2 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="healing">Healing</SelectItem>
                      <SelectItem value="salvation">Salvation</SelectItem>
                      <SelectItem value="family">Family</SelectItem>
                      <SelectItem value="finance">Provision</SelectItem>
                      <SelectItem value="thanksgiving">Thanksgiving</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="message">Your Prayer Request *</Label>
                  <Textarea id="message" data-testid="prayer-message" value={form.message} onChange={update("message")} className="mt-2 min-h-[140px] rounded-sm" placeholder="Share your request…" />
                </div>
                <Button type="submit" disabled={submitting} data-testid="prayer-submit" className="w-full rounded-sm bg-brand-orange py-6 font-display uppercase tracking-wide hover:bg-brand-orange-600">
                  {submitting ? "Sending…" : (<><Send className="mr-2 h-4 w-4" /> Submit Prayer Request</>)}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrayerRequests;
